import axios from "axios";
// import { jwtStore } from "@/stores/jwt"
// import { userStore } from "@/stores/user"

declare module "axios" {
  interface AxiosRequestConfig {
    // A chamada trata o próprio 404 (ex.: página de ensaio que já veio do SSR
    // e deve continuar na tela), então o interceptor não manda para a home.
    semRedirect404?: boolean;
  }
}

// In dev we rely on Nuxt's devProxy (see nuxt.config.ts `nitro.devProxy`)
// which forwards /api/* to the Laravel backend. A root-relative baseURL
// keeps the browser same-origin with Nuxt — no CORS preflight, no
// Access-Control-* headers needed. In production the Nuxt SSR server
// (or the deployed reverse proxy) handles /api/* the same way.
// VITE_API_LOCAL=1 (só em dev) usa o Laravel local na porta 80: no navegador
// pelo devProxy (/api), no SSR direto. Sem a variável, nada muda.
const apiLocal = import.meta.dev && import.meta.env.VITE_API_LOCAL === "1";

const api = axios.create({
  withCredentials: true,
  //baseURL: '/api/'
  baseURL: apiLocal
    ? import.meta.server
      ? "http://127.0.0.1/api/"
      : "/api/"
    : "https://api.belladasemana.com.br/api/",
});

api.interceptors.request.use(
  async function (config) {
    if (import.meta.client) {
      const store = jwtStore();
      if (store.jwt) config.headers.Authorization = `Bearer ${store.jwt}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    // The backend may transparently refresh an expired-but-refreshable JWT
    // and return the new token alongside any normal payload. Pick it up here
    // so consumers (user store, page components) never need to know about it.
    if (import.meta.client && response?.data?.refreshed_token) {
      const store = jwtStore();
      store.setJwt(response.data.refreshed_token);
    }
    return response;
  },
  async (error) => {
    if (error?.response?.status === 404) {
      // Guardado com import.meta.client: no SSR não há `window`, e um 404 aqui
      // (ex.: durante o fetch de planos no useAsyncData) lançaria ReferenceError
      // e derrubaria o render do servidor. No cliente, comportamento inalterado,
      // exceto quando a chamada pede `semRedirect404`.
      if (import.meta.client && !error.config?.semRedirect404) window.location.href = "/";
      return Promise.reject(error);
    }

    if (error?.response?.status !== 401 || error.code === "ERR_NETWORK") {
      return Promise.reject(error);
    }

    const store = jwtStore();
    if (!store.jwt) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as typeof error.config & {
      _retriedAfterRefresh?: boolean;
    };

    // The refresh endpoint itself is exempt from the refresh-retry path. A 401
    // from `auth/refreshJwt` means the session is genuinely expired — trying to
    // refresh again here would re-enter this handler and `await` the very
    // in-flight refresh promise we are currently rejecting, deadlocking the
    // app boot (frozen/black screen for logged-in users with a stale token).
    // Log out deterministically instead.
    if (
      typeof originalRequest?.url === "string" &&
      originalRequest.url.includes("auth/refreshJwt")
    ) {
      await store.jwtLogout();
      return Promise.reject(error);
    }

    // One-shot retry guard: if we already refreshed once for this request and
    // it still came back 401, give up instead of looping through more refreshes.
    if (originalRequest._retriedAfterRefresh) {
      await store.jwtLogout();
      return Promise.reject(error);
    }

    try {
      const { data } = await store.refreshOnce();
      const newToken = data?.authorisation?.token;
      if (!newToken) {
        await store.jwtLogout();
        return Promise.reject(error);
      }
      await store.setJwt(newToken);
      originalRequest._retriedAfterRefresh = true;
      return await api(originalRequest);
    } catch (refreshError) {
      await store.jwtLogout();
      return Promise.reject(error);
    }
  }
);

export default api;
